'use client';
import { useEffect } from 'react';

/**
 * Component that forcefully prevents text selection on all elements
 * except inputs, textareas, and contenteditable elements.
 *
 * This uses JavaScript to apply inline styles which have higher specificity
 * than global CSS and can override MUI's CSS-in-JS styles.
 */
export default function TextSelectionBlocker() {
    useEffect(() => {
        // Elements that should ALLOW text selection
        const selectableSelectors = [
            'input',
            'textarea',
            '[contenteditable="true"]',
            // Also allow selection when content is expanded (has userSelect: text in sx)
            '[style*="user-select: text"]',
            '[style*="user-select:text"]',
        ];

        const shouldAllowTextSelection = (element: HTMLElement): boolean => {
            // Check if element is an input/textarea/contenteditable
            const isFormElement = selectableSelectors.some(selector => {
                if (selector.startsWith('[')) {
                    return element.matches(selector);
                }
                return element.tagName.toLowerCase() === selector;
            });

            if (isFormElement) {
                return true;
            }

            // Check if this element has inline style allowing text selection
            if (element.style.userSelect === 'text') {
                return true;
            }

            // Check computed style to see if CSS has set userSelect to text
            // This handles MUI sx prop: userSelect: 'text !important'
            const computedStyle = window.getComputedStyle(element);
            if (computedStyle.userSelect === 'text') {
                return true;
            }

            // Check if inside a parent that allows text selection
            // (traverse up to 5 levels to handle nested elements)
            let parent = element.parentElement;
            let levels = 0;
            while (parent && levels < 5) {
                if (parent.style.userSelect === 'text') {
                    return true;
                }
                const parentComputed = window.getComputedStyle(parent);
                if (parentComputed.userSelect === 'text') {
                    return true;
                }
                parent = parent.parentElement;
                levels++;
            }

            return false;
        };

        const isClickableElement = (element: HTMLElement): boolean => {
            // Check if element is a button, link, or has click handler
            const clickableTags = ['a', 'button', 'input', 'select', 'textarea'];
            const tagName = element.tagName.toLowerCase();

            if (clickableTags.includes(tagName)) {
                return true;
            }

            // Check if element has role="button" or onclick attribute
            if (element.getAttribute('role') === 'button' || element.onclick) {
                return true;
            }

            // Check if element has cursor: pointer set
            const computedStyle = window.getComputedStyle(element);
            if (computedStyle.cursor === 'pointer') {
                return true;
            }

            // Check MUI clickable components
            if (element.classList.contains('MuiButton-root') ||
                element.classList.contains('MuiIconButton-root') ||
                element.classList.contains('MuiLink-root') ||
                element.classList.contains('MuiListItemButton-root') ||
                element.classList.contains('MuiCardActionArea-root')) {
                return true;
            }

            return false;
        };

        const applyTextBlockingStyles = (element: HTMLElement) => {
            if (shouldAllowTextSelection(element)) {
                return; // Don't block text selection on this element
            }

            // Apply text blocking styles via inline CSS
            const style = element.style as any;
            style.webkitUserSelect = 'none';
            style.mozUserSelect = 'none';
            style.msUserSelect = 'none';
            style.userSelect = 'none';
            style.webkitTouchCallout = 'none';

            // Fix cursor: set to default for non-clickable elements, keep pointer for clickable ones
            if (!element.style.cursor) { // Only set if not already set
                if (isClickableElement(element)) {
                    style.cursor = 'pointer';
                } else {
                    style.cursor = 'default';
                }
            }

            // Also add HTML attributes for older browsers
            element.setAttribute('unselectable', 'on');
        };

        // Apply to all existing elements
        const allElements = document.querySelectorAll('*');
        allElements.forEach(el => applyTextBlockingStyles(el as HTMLElement));

        // Watch for new elements being added to the DOM
        const observer = new MutationObserver((mutations) => {
            mutations.forEach((mutation) => {
                mutation.addedNodes.forEach((node) => {
                    if (node instanceof HTMLElement) {
                        applyTextBlockingStyles(node);
                        // Also apply to all children
                        const children = node.querySelectorAll('*');
                        children.forEach(child => applyTextBlockingStyles(child as HTMLElement));
                    }
                });
            });
        });

        observer.observe(document.body, {
            childList: true,
            subtree: true,
        });

        return () => {
            observer.disconnect();
        };
    }, []);

    return null; // This component doesn't render anything
}
